/**
 * Shared quick-add → cart drawer flow for product cards, PDP sticky bar, etc.
 */

import { CartAddEvent } from '@theme/events';

const ADDING_LABEL =
  typeof Theme !== 'undefined' && Theme.translations?.adding_to_cart
    ? Theme.translations.adding_to_cart
    : 'Adding...';

const SUCCESS_FLASH_MS = 380;

function isPlpQuickAddButton(button) {
  return Boolean(button?.classList.contains('quick-add-btn') && button.closest('.evry-plp'));
}

/**
 * @param {HTMLElement} button
 */
function ensurePlpSpinner(button) {
  if (!isPlpQuickAddButton(button)) return;
  if (button.querySelector('.plp-atc-spinner')) return;

  const spinner = document.createElement('span');
  spinner.className = 'plp-atc-spinner';
  spinner.setAttribute('aria-hidden', 'true');
  button.appendChild(spinner);
}

/**
 * @param {HTMLElement | null | undefined} button
 */
function isPremiumLoadingButton(button) {
  return Boolean(
    button?.closest('#sticky-product-bar') || button?.classList.contains('pdp-sticky-bar__quick-add-btn')
  );
}

/**
 * @param {HTMLElement} button
 * @returns {Promise<void>}
 */
function flashButtonSuccess(button) {
  button.classList.remove('quick-add-btn--pending', 'loading');
  button.classList.add('quick-add-btn--success');
  button.setAttribute('aria-busy', 'false');

  return new Promise((resolve) => {
    window.setTimeout(() => {
      button.classList.remove('quick-add-btn--success');
      button.removeAttribute('disabled');
      resolve();
    }, SUCCESS_FLASH_MS);
  });
}

/**
 * @param {HTMLElement | null | undefined} button
 * @param {boolean} isLoading
 */
function setButtonLoading(button, isLoading) {
  if (!(button instanceof HTMLElement)) return;

  const stockStatus = button.querySelector('.stock-status');
  const label = button.querySelector('.quick-add-btn__label');
  const usePremiumLoading = isPremiumLoadingButton(button);

  if (isLoading) {
    button.classList.add('loading');
    button.setAttribute('disabled', 'disabled');
    button.setAttribute('aria-busy', 'true');
    button.classList.remove('quick-add-btn--success');

    if (usePremiumLoading) {
      button.classList.add('quick-add-btn--pending');
      return;
    }

    if (isPlpQuickAddButton(button)) {
      ensurePlpSpinner(button);
      return;
    }

    if (stockStatus instanceof HTMLElement) {
      if (!stockStatus.dataset.quickAddOriginalText) {
        stockStatus.dataset.quickAddOriginalText = stockStatus.textContent?.trim() || '';
        stockStatus.dataset.quickAddOriginalClass = stockStatus.className;
      }
      stockStatus.textContent = ADDING_LABEL;
      stockStatus.className = 'stock-status adding';
      return;
    }

    if (label instanceof HTMLElement) {
      if (!button.dataset.quickAddOriginalLabel) {
        button.dataset.quickAddOriginalLabel = label.textContent?.trim() || '';
      }
      label.textContent = ADDING_LABEL;
      return;
    }

    if (!button.dataset.quickAddOriginalText) {
      button.dataset.quickAddOriginalText = button.textContent?.trim() || '';
    }
    button.textContent = ADDING_LABEL;
    return;
  }

  button.classList.remove('loading', 'quick-add-btn--pending');
  button.removeAttribute('disabled');
  button.removeAttribute('aria-busy');

  if (isPlpQuickAddButton(button)) {
    button.style.removeProperty('width');
    button.style.removeProperty('min-width');
    button.style.removeProperty('height');
    button.style.removeProperty('min-height');
  }

  if (stockStatus instanceof HTMLElement && stockStatus.dataset.quickAddOriginalText !== undefined) {
    stockStatus.textContent = stockStatus.dataset.quickAddOriginalText;
    stockStatus.className = stockStatus.dataset.quickAddOriginalClass || 'stock-status';
    delete stockStatus.dataset.quickAddOriginalText;
    delete stockStatus.dataset.quickAddOriginalClass;
    return;
  }

  if (label instanceof HTMLElement && button.dataset.quickAddOriginalLabel) {
    label.textContent = button.dataset.quickAddOriginalLabel;
    delete button.dataset.quickAddOriginalLabel;
    return;
  }

  if (button.dataset.quickAddOriginalText) {
    button.textContent = button.dataset.quickAddOriginalText;
    delete button.dataset.quickAddOriginalText;
  }
}

/**
 * @param {HTMLElement} wrapper
 */
function resetWrapperButtons(wrapper) {
  if (!(wrapper instanceof HTMLElement)) return;

  wrapper.querySelectorAll('[data-add-to-cart], [data-single-add-to-cart]').forEach((button) => {
    setButtonLoading(button, false);
  });
}

function resetAllQuickAddButtons() {
  document.querySelectorAll('.quick-add-wrapper').forEach((wrapper) => {
    if (wrapper instanceof HTMLElement) {
      resetWrapperButtons(wrapper);
    }
  });
}

function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      ids.add(component.dataset.sectionId);
    }
  });
  return [...ids];
}

/**
 * @param {HTMLElement | null | undefined} drawer
 */
export function openCartDrawer(drawer = document.querySelector('cart-drawer-component, cart-drawer')) {
  if (!(drawer instanceof HTMLElement)) return;

  if (typeof drawer.open === 'function') {
    drawer.open();
  } else if (typeof drawer.show === 'function') {
    drawer.show();
  } else {
    drawer.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
  }
}

export function closeAllPopups() {
  resetAllQuickAddButtons();

  document.querySelectorAll('.plp-variant-panel-open').forEach((el) => {
    el.classList.remove('plp-variant-panel-open');
  });

  document.querySelectorAll('.variant-popup').forEach((popup) => {
    if (!(popup instanceof HTMLElement)) return;
    popup.hidden = true;
    if (popup.classList.contains('plp-variant-dropdown')) {
      popup.style.removeProperty('display');
    } else {
      popup.style.display = 'none';
    }
  });

  document.querySelectorAll('.popup-backdrop').forEach((backdrop) => {
    if (backdrop instanceof HTMLElement) {
      backdrop.style.display = 'none';
    }
  });

  document.body.style.overflow = '';
}

/**
 * @param {number} itemCount
 */
export function updateCartBubbles(itemCount) {
  document.querySelectorAll('[data-testid="cart-bubble"], [ref="cartBubbleCount"]').forEach((el) => {
    el.textContent = String(itemCount);
  });

  document.querySelectorAll('.cart-bubble .visually-hidden').forEach((hidden) => {
    hidden.textContent = `Total items in cart: ${itemCount}`;
  });

  document.querySelectorAll('cart-items-component [ref="cartItemCount"]').forEach((el) => {
    el.textContent = String(itemCount);
  });
}

export async function refreshCartItemsSections() {
  const components = document.querySelectorAll('cart-items-component[data-section-id]');
  if (!components.length) return;

  const sectionIds = [...new Set(Array.from(components).map((c) => c.dataset.sectionId).filter(Boolean))];
  if (!sectionIds.length) return;

  const url = `${window.location.pathname}?sections=${sectionIds.join(',')}`;
  const response = await fetch(url);
  const sections = await response.json();

  sectionIds.forEach((sectionId) => {
    const htmlString = sections[sectionId];
    if (!htmlString) return;

    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    const newComponent =
      doc.querySelector(`cart-items-component[data-section-id="${sectionId}"]`) ||
      doc.querySelector('cart-items-component');

    if (!newComponent) return;

    document.querySelectorAll(`cart-items-component[data-section-id="${sectionId}"]`).forEach((current) => {
      current.innerHTML = newComponent.innerHTML;
    });
  });
}

export async function updateCartUI() {
  const [cart] = await Promise.all([
    fetch(`/cart.js?timestamp=${Date.now()}`).then((response) => response.json()),
    refreshCartItemsSections(),
  ]);
  updateCartBubbles(cart.item_count);
  return cart;
}

export function triggerCartUpdates() {
  document.dispatchEvent(new CustomEvent('cart:build'));
  document.dispatchEvent(new CustomEvent('cart:refresh'));
  document.dispatchEvent(new CustomEvent('cart:updated'));

  if (typeof window.jQuery !== 'undefined') {
    window.jQuery(document).trigger('ajaxCart.afterCartLoad');
  }
}

/**
 * @param {HTMLElement | null | undefined} button
 */
function getQuickAddQuantity(button) {
  const stickyBar = button?.closest('#sticky-product-bar');
  if (!(stickyBar instanceof HTMLElement)) return 1;

  const stickyInput = stickyBar.querySelector('.pdp-sticky-bar__quantity input[type="number"]');
  if (stickyInput instanceof HTMLInputElement) {
    const qty = parseInt(stickyInput.value, 10);
    if (qty > 0) return qty;
  }

  return 1;
}

/**
 * @param {string | number} variantId
 * @param {{ button?: HTMLElement | null, closePopups?: boolean, quantity?: number }} [options]
 */
export async function addVariantToCart(variantId, options = {}) {
  const { button = null, closePopups = true, quantity = getQuickAddQuantity(button) } = options;
  const wrapper = button?.closest('.quick-add-wrapper');

  if (button?.classList.contains('loading')) return;
  if (wrapper instanceof HTMLElement && wrapper.dataset.quickAddInFlight === 'true') return;

  if (wrapper instanceof HTMLElement) {
    wrapper.dataset.quickAddInFlight = 'true';
  }

  setButtonLoading(button, true);

  const sectionIds = getCartSectionIds();
  /** @type {{ items: Array<{ id: number, quantity: number }>, sections?: string, sections_url?: string }} */
  const payload = {
    items: [{ id: Number(variantId), quantity: Number(quantity) || 1 }],
  };

  if (sectionIds.length) {
    payload.sections = sectionIds.join(',');
    payload.sections_url = window.location.pathname;
  }

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.description || result.message || 'Add to cart failed');
    }

    if (closePopups) {
      closeAllPopups();
    }

    // Update the cart drawer's content (via the CartAddEvent -> morph) before
    // the drawer is opened. Opening the drawer first and only updating its
    // contents afterwards (e.g. after the button's success-flash delay) causes
    // the drawer to render mid-update, producing a visible layout glitch the
    // first time the cart goes from empty to non-empty.
    document.dispatchEvent(
      new CartAddEvent({}, String(variantId), {
        source: 'product-form-component',
        itemCount: Number(quantity) || 1,
        variantId: String(variantId),
        sections: result.sections,
      })
    );

    triggerCartUpdates();

    if (!result.sections) {
      await updateCartUI().catch((err) => console.error('Cart UI refresh error:', err));
    }

    if (button instanceof HTMLElement && isPremiumLoadingButton(button)) {
      const successPromise = flashButtonSuccess(button);
      openCartDrawer();
      await successPromise;
    } else if (button instanceof HTMLElement && isPlpQuickAddButton(button)) {
      setButtonLoading(button, false);
      openCartDrawer();
    } else {
      setButtonLoading(button, false);
      openCartDrawer();
    }
  } catch (error) {
    console.error('Quick add cart error:', error);
    setButtonLoading(button, false);
    throw error;
  } finally {
    if (wrapper instanceof HTMLElement) {
      delete wrapper.dataset.quickAddInFlight;
    }
  }
}

/**
 * @param {HTMLElement} wrapper
 */
export function initQuickAddWrapper(wrapper) {
  if (!(wrapper instanceof HTMLElement)) return;
  if (wrapper.dataset.quickAddInitialized === 'true') return;
  if (wrapper.classList.contains('plp-quick-add-wrapper')) return;

  wrapper.dataset.quickAddInitialized = 'true';

  const hasMultipleVariants = wrapper.querySelectorAll('.variant-item').length > 0;

  if (hasMultipleVariants) {
    const openBtn = wrapper.querySelector('[data-open-popup]');
    const popup = wrapper.querySelector('.variant-popup');
    const backdrop = wrapper.querySelector('.popup-backdrop');
    const closeBtn = wrapper.querySelector('[data-close-popup]');

    const closePopup = () => {
      resetWrapperButtons(wrapper);
      if (!(popup instanceof HTMLElement)) return;
      popup.hidden = true;
      popup.style.display = 'none';
      if (backdrop instanceof HTMLElement) backdrop.style.display = 'none';
      document.body.style.overflow = '';
    };

    if (openBtn instanceof HTMLButtonElement && popup instanceof HTMLElement) {
      openBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        closeAllPopups();
        resetWrapperButtons(wrapper);
        popup.hidden = false;
        popup.style.display = 'block';
        if (backdrop instanceof HTMLElement) backdrop.style.display = 'block';
        document.body.style.overflow = 'hidden';
      });
    }

    if (backdrop instanceof HTMLElement) {
      backdrop.addEventListener('click', closePopup);
    }

    if (closeBtn instanceof HTMLButtonElement) {
      closeBtn.addEventListener('click', closePopup);
    }

    wrapper.querySelectorAll('[data-add-to-cart]:not(:disabled)').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const id = button.dataset.variantId;
        if (!id) return;
        addVariantToCart(id, { button, closePopups: true });
      });
    });
  } else {
    const singleBtn = wrapper.querySelector('[data-single-add-to-cart]');
    if (singleBtn instanceof HTMLButtonElement) {
      singleBtn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const id = singleBtn.dataset.variantId;
        if (!id) return;
        addVariantToCart(id, { button: singleBtn, closePopups: true });
      });
    }
  }
}

function initQuickAddCart() {
  document.querySelectorAll('.quick-add-wrapper').forEach(initQuickAddWrapper);

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.quick-add-wrapper')) {
      closeAllPopups();
    }
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.classList.contains('quick-add-wrapper')) {
          initQuickAddWrapper(node);
        }
        node.querySelectorAll('.quick-add-wrapper').forEach(initQuickAddWrapper);
      });
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

window.ThemeQuickAddCart = {
  addVariantToCart,
  closeAllPopups,
  initQuickAddWrapper,
  initQuickAddCart,
  openCartDrawer,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuickAddCart, { once: true });
} else {
  initQuickAddCart();
}
